import { useEffect, useState, useCallback } from "react";
import { Loader2, RefreshCw, AlertTriangle, Copy, Check } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";

/**
 * Webhook Errors admin tab — debug channel for Stripe webhook failures.
 *
 * Data source: `/api/admin/webhook-errors` returns the most recent rows
 * from MongoDB's `webhook_errors` collection. Each row carries the full
 * Python traceback that fired inside the webhook handler, along with the
 * Stripe `event_id`, `session_id`, and the stage at which the error
 * happened (`signature_verification`, `event_parse`, `post_signature_processing`,
 * or `missing_secret`).
 *
 * This exists because production pod logs are not directly accessible
 * from the agent / development tooling — persisting tracebacks to DB is
 * the only debugging surface we have for live Stripe events.
 */
export default function AdminWebhookErrorsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await api.adminWebhookErrors(50);
      setItems(res?.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "טעינה נכשלה");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="admin-webhook-errors-tab" dir="rtl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" strokeWidth={1.5} />
            שגיאות Webhook (Stripe)
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            כל exception של ה־webhook נשמר כאן עם traceback מלא. הרצף ממוין מהחדש לישן.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          className="rounded-full gap-1.5"
          data-testid="webhook-errors-refresh"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          רענן
        </Button>
      </div>

      {err && (
        <div
          data-testid="webhook-errors-error"
          className="rounded-sm border-r-4 border-red-500/70 bg-red-500/10 px-4 py-3 text-sm text-red-800"
        >
          שגיאה בטעינה: {err}
        </div>
      )}

      {!loading && !err && items.length === 0 && (
        <div
          data-testid="webhook-errors-empty"
          className="rounded-sm border border-border/60 bg-card/40 px-4 py-8 text-sm text-center text-muted-foreground"
        >
          אין שגיאות webhook להציג. (אם הכל עובד — זה הסטטוס הצפוי 🎉)
        </div>
      )}

      <div className="space-y-3">
        {items.map((row, i) => (
          <ErrorRow key={row.id || i} row={row} idx={i} />
        ))}
      </div>
    </div>
  );
}

function ErrorRow({ row, idx }) {
  const [copied, setCopied] = useState(false);

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(row, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  // Color the stage badge by severity — signature failures are mostly
  // benign (Stripe retries with fresh signatures), post-signature is
  // the dangerous category (DB / email / cert generation crashes).
  const stage = row.stage || "(unknown)";
  const isReceiptEmail = stage.startsWith("receipt_email");
  const stageTone =
    stage === "post_signature_processing" ? "bg-red-500/15 text-red-700 border-red-500/30" :
    stage === "missing_secret"            ? "bg-amber-500/15 text-amber-700 border-amber-500/30" :
    isReceiptEmail                        ? "bg-orange-500/15 text-orange-700 border-orange-500/30" :
    "bg-foreground/10 text-foreground/70 border-foreground/20";

  return (
    <details
      open={idx < 3}
      className="group rounded-sm border border-border/60 bg-card/40 overflow-hidden"
      data-testid={`webhook-error-row-${idx}`}
    >
      <summary className="cursor-pointer px-4 py-3 select-none flex items-center justify-between gap-3 hover:bg-card/60">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${stageTone}`}>
              {stage}
            </span>
            <span className="text-sm font-heading text-foreground truncate">
              {row.exc_type || "Exception"}: {row.exc_message || ""}
            </span>
          </div>
          <div className="mt-1 text-[11px] text-muted-foreground font-mono flex gap-3 flex-wrap" dir="ltr">
            <span>{row.ts || "—"}</span>
            {row.event_type && <span>type={row.event_type}</span>}
            {row.event_id && <span>event={row.event_id}</span>}
            {row.session_id && <span>session={row.session_id}</span>}
          </div>
        </div>
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); copyAll(); }}
          className="shrink-0 text-xs px-2 py-1 rounded-full bg-foreground/5 hover:bg-foreground/10 inline-flex items-center gap-1"
          data-testid={`webhook-error-copy-${idx}`}
        >
          {copied
            ? (<><Check className="h-3 w-3 text-green-600" /> הועתק</>)
            : (<><Copy className="h-3 w-3" /> העתק</>)}
        </button>
      </summary>

      <div className="px-4 py-3 border-t border-border/60 bg-foreground/5 text-xs">
        {isReceiptEmail && (
          <div
            className="mb-3 p-3 rounded-sm border border-orange-500/30 bg-orange-500/5 space-y-1"
            data-testid={`webhook-error-resend-details-${idx}`}
          >
            <div className="text-[11px] font-heading text-orange-700 mb-1">
              פרטי כשל שליחת קבלה (Resend)
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 font-mono text-[11px] text-foreground/80" dir="ltr">
              {row.resend_from         && <div>from: {row.resend_from}</div>}
              {row.resend_to_domain    && <div>to_domain: {row.resend_to_domain}</div>}
              {row.resend_code         && <div>code: {row.resend_code}</div>}
              {row.resend_status_code  && <div>status: {row.resend_status_code}</div>}
              {row.receipt_number      && <div>receipt: {row.receipt_number}</div>}
              {row.session_id          && <div>session: {row.session_id}</div>}
            </div>
            {row.resend_suggestion && (
              <div className="mt-2 text-[11px] text-orange-700/90">
                המלצה: <span dir="ltr">{row.resend_suggestion}</span>
              </div>
            )}
          </div>
        )}
        {row.traceback ? (
          <pre
            dir="ltr"
            className="whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed text-foreground/85 max-h-96 overflow-auto"
            data-testid={`webhook-error-traceback-${idx}`}
          >{row.traceback}</pre>
        ) : (
          <span className="text-muted-foreground">אין traceback ברשומה זו</span>
        )}

        {row.headers && Object.keys(row.headers).length > 0 && (
          <details className="mt-3" open>
            <summary className="cursor-pointer text-[11px] font-heading text-foreground/70 mb-1">
              Stripe headers
            </summary>
            <pre dir="ltr" className="font-mono text-[10px] text-foreground/70 whitespace-pre-wrap break-all">
              {JSON.stringify(row.headers, null, 2)}
            </pre>
          </details>
        )}

        {row.body_preview && (
          <details className="mt-3">
            <summary className="cursor-pointer text-[11px] font-heading text-foreground/70 mb-1">
              Body preview (first 2KB)
            </summary>
            <pre dir="ltr" className="font-mono text-[10px] text-foreground/70 whitespace-pre-wrap break-all max-h-40 overflow-auto">
              {row.body_preview}
            </pre>
          </details>
        )}
      </div>
    </details>
  );
}
